import {
  Box,
  Button,
  Container,
  ScrollView,
  SizableText,
  SmartLink,
  Spinner,
  Stack,
  XStack,
  YStack,
  colors,
  isWeb,
  shadowStandard,
  useSafeArea,
} from '@my/ui'
import { Share2 } from '@tamagui/lucide-icons'
import { MainLayout, Meta, NativePageNavigation, StructuredMeta } from 'app/components'
import config from 'app/config'
import { ErrorScreen404 } from 'app/features/errors/screens'
import { ShareModal } from 'app/features/sharing/components'
import { OfferQueryOutput } from './queries'
import { OfferBlockRenderer } from '../../blocks'
import { getOfferBlockMappings } from '../../blocks/block-mappings'
import { RedeemOfferButton, SaveOfferButton } from '../../containers'
import { SolitoImage } from 'solito/image'
import { useIFrame } from 'app/provider/iframe'

export interface OfferScreenProps {
  loading: boolean
  offer?: OfferQueryOutput
  error: any
  refetch: any
  slug: string
}

export const OfferScreen = (props: OfferScreenProps) => {
  const insets = useSafeArea()
  const { isIframe } = useIFrame()

  if (props.loading) {
    return (
      <MainLayout>
        <Container flex={1} justifyContent="center">
          <Spinner size="large" />
        </Container>
      </MainLayout>
    )
  }

  const offer = props.offer?.Offers?.docs[0]
  const shareLinkUrl = config.siteUrl + `/discounts/${offer?.brand.slug}/${offer?.slug}`

  if (!offer) {
    return <ErrorScreen404 />
  }

  const renderOffer = () => {
    return (
      <Container backgroundColor="$white" bblr="$3" bbrr="$3">
        <YStack position="relative" width="100%">
          <XStack position="relative" width="100%" flex={1}>
            <Stack w="100%" aspectRatio={39 / 20} $gtSm={{ aspectRatio: 5 / 2 }}>
              <SolitoImage
                src={offer.featuredImage?.url}
                alt={offer.name}
                fill
                contentFit="cover"
                priority
                sizes="(max-width: 900px) 100vw, 900px"
              />
            </Stack>

            <XStack zIndex="$2" position="absolute" bottom="$3" right="$4">
              <SaveOfferButton size={30} offerId={offer.id} isBookmarked={offer.isBookmarked} />
            </XStack>
          </XStack>

          {offer.brand.logo && (
            <SmartLink
              Component={Stack}
              href={`/discounts/${offer.brand.slug}`}
              position="absolute"
              aspectRatio={1}
              bottom={-24}
              left="$3.5"
              bg="$white"
              boc="$white"
              bw="$2"
              br="$3"
              width="100%"
              w={88}
              h={88}
              zIndex="$1"
              $gtSm={{ left: '$5' }}
              hoverStyle={{ opacity: 0.95 }}
              pressStyle={{ opacity: 0.85 }}
              focusStyle={{ opacity: 0.8 }}
              {...shadowStandard}
            >
              <SolitoImage
                src={offer.brand.logo.url}
                alt={offer.brand.name}
                contentFit="contain"
                fill
                sizes="88px"
              />
            </SmartLink>
          )}
        </YStack>

        <Container px="$3.5" pt="$8" pb="$6" gap="$3.5" ai="flex-start" gutter>
          <YStack gap="$2" w="100%">
            <XStack jc="space-between" ai="flex-start" gap="$3.5" width="100%">
              <SizableText
                tag="h1"
                ta="left"
                size="$7"
                fow="600"
                $gtSm={{ size: '$9', fow: '600' }}
              >
                {offer.name}
              </SizableText>
              {isWeb && shareLinkUrl && (
                <ShareModal
                  href={shareLinkUrl}
                  subject="Check out this offer"
                  trigger={
                    <Button size="$3" px="$2" plain icon={<Share2 size={24} />} fontWeight="500">
                      Share
                    </Button>
                  }
                />
              )}
            </XStack>

            {offer.description && (
              <SizableText color="$overlay11" size="$4" fow="500">
                {offer.description}
              </SizableText>
            )}
          </YStack>
          {isWeb && <RedeemOfferButton offerData={props.offer} isEmbed={isIframe} />}
        </Container>

        {offer.content && offer.content.length > 0 && (
          <YStack pb="$3.5" $gtSm={{ pb: '$6' }}>
            <OfferBlockRenderer
              additionalComponents={getOfferBlockMappings()}
              content={offer.content}
              extraProps={{ offer: props.offer }}
            />
          </YStack>
        )}
      </Container>
    )
  }

  return (
    <MainLayout>
      {offer && (
        <>
          <Meta
            title={offer.meta?.title || offer.name}
            description={offer.meta?.description || offer.description}
            image={offer.featuredImage?.url}
          />
          <StructuredMeta
            type="article"
            title={offer.name}
            description={offer.description}
            image={offer.featuredImage?.url}
            date={offer.updatedAt}
            url={`${config.siteUrl}/discounts/${offer.brand.slug}/${offer.slug}`}
          />
        </>
      )}
      <NativePageNavigation
        title={offer?.name}
        Controls={[
          offer && (
            <SaveOfferButton
              key="save"
              color={colors.black}
              size={24}
              offerId={offer.id}
              isBookmarked={offer.isBookmarked}
            />
          ),
          <ShareModal
            key="share"
            href={shareLinkUrl}
            subject="Check out this offer"
            trigger={<Button size="$3" px="$2" plain icon={<Share2 size={24} />} />}
          />,
        ]}
      />

      <Container flex={1} size="base" gutter="gtSm" position="relative" spaceBottom={isWeb}>
        <ScrollView>{renderOffer()}</ScrollView>
        {offer && !isWeb && (
          <Box pt="$4" pb={16 + insets.bottom} position="relative" left="$0" w="100%">
            <RedeemOfferButton offerData={props.offer} />
          </Box>
        )}
      </Container>
    </MainLayout>
  )
}
